import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ProductSearch } from "./group-sale/ProductSearch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { getProductName } from "../history/ProductUtils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface DeleteProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stockItems: any[];
  onConfirmDelete: (productId: string) => void;
}

export const DeleteProductDialog = ({
  open,
  onOpenChange,
  stockItems,
  onConfirmDelete,
}: DeleteProductDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const queryClient = useQueryClient();

  const filteredProducts = stockItems?.filter((item) =>
    getProductName(item).toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleDelete = async () => {
    if (selectedProduct) {
      try {
        // Check if product has associated transactions
        const { data: transactions, error: transactionError } = await supabase
          .from('poultry_transactions')
          .select('id')
          .eq('stock_item_id', selectedProduct.id)
          .limit(1);

        if (transactionError) throw transactionError;

        if (transactions && transactions.length > 0) {
          toast.error("Ce produit ne peut pas être supprimé car il a des transactions associées");
          setShowConfirmation(false);
          return;
        }

        onConfirmDelete(selectedProduct.id);
        
        // Invalidate relevant queries to trigger UI updates
        queryClient.invalidateQueries({ queryKey: ['stockItems'] });
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        
        setSelectedProduct(null);
        setSearchQuery("");
        setShowConfirmation(false);
        onOpenChange(false);
        
        toast.success("Produit supprimé avec succès");
      } catch (error: any) {
        console.error("Erreur lors de la vérification des transactions:", error);
        toast.error("Une erreur est survenue lors de la suppression");
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer un produit</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <ProductSearch
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />

            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                    selectedProduct?.id === product.id
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 hover:border-red-200 hover:bg-red-50"
                  }`}
                  onClick={() => setSelectedProduct(product)}
                >
                  <div className="font-medium">{getProductName(product)}</div>
                  <div className="text-sm text-gray-500">
                    Quantité: {product.quantity}
                  </div>
                </div>
              ))}
            </div>

            {selectedProduct && (
              <Alert className="bg-red-50 border-red-200 text-red-800">
                <AlertDescription>
                  Vous avez sélectionné le produit : {getProductName(selectedProduct)}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => setShowConfirmation(true)}
              disabled={!selectedProduct}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le produit sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};